import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { interviewRuntimeAPI } from '../api/interviewRuntimeAPI';
import InterviewUpgradeGate, { useGateStatus } from './InterviewUpgradeGate';
import './InterviewBrowse.css';

/**
 * One workspace's leaves (e.g. Medicine -> Edinburgh Medicine, Oxford
 * Medicine, ...). The :workspace route param is the same lowercase slug
 * used in STANDARD_TOOLS' three new tile paths; matched case-insensitively
 * against GET /api/interviews/workspaces' own `workspace` field ("Medicine",
 * "Dentistry", "Oxbridge") rather than re-fetching per workspace — one call
 * already returns every leaf nested under its workspace.
 */
/** "16 live interviews · 32 written", from the counts /workspaces gives. */
function courseCounts(leaf) {
  const live = leaf.live_interviews || 0;
  const written = leaf.written_interviews ?? leaf.paper_count ?? 0;
  const parts = [];
  if (live) parts.push(`${live} live interview${live === 1 ? '' : 's'}`);
  if (written) parts.push(`${written} written`);
  return parts.join(' · ');
}

export default function InterviewLeavesPage() {
  const { workspace: workspaceSlug } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | ready | upgrade | error
  const [gateCode, statusFor] = useGateStatus();
  const [workspace, setWorkspace] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    interviewRuntimeAPI.getWorkspaces()
      .then((data) => {
        if (cancelled) return;
        const match = (data.workspaces || []).find(
          (w) => (w.workspace || '').toLowerCase() === (workspaceSlug || '').toLowerCase(),
        );
        if (!match) {
          setStatus('error');
          return;
        }
        setWorkspace(match);
        setStatus('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus(statusFor(err));
      });
    return () => { cancelled = true; };
  }, [workspaceSlug]);

  if (status === 'loading') {
    return <div className="ib"><div className="ib__state">Loading…</div></div>;
  }
  if (status === 'upgrade') {
    return <div className="ib"><InterviewUpgradeGate code={gateCode} /></div>;
  }
  if (status === 'error' || !workspace) {
    return (
      <div className="ib">
        <div className="ib__state ib__state--error">
          Could not load this workspace. <Link to="/apps">Back to apps</Link>
        </div>
      </div>
    );
  }

  const leaves = workspace.leaves || [];

  return (
    <div className="ib">
      <div className="ib__crumbs"><Link to="/apps">Apps</Link> / {workspace.workspace}</div>
      <h1 className="ib__title">{workspace.workspace} interviews</h1>
      <p className="ib__lede">
        {leaves.length
          ? 'Choose a course to see its live interviews and mock interview papers.'
          : 'No courses here yet.'}
      </p>

      {leaves.length > 0 && (
        <div className="ib__grid">
          {leaves.map((leaf) => (
            <button
              key={leaf.leaf_key}
              type="button"
              className="ib__card"
              onClick={() => navigate(`/interviews/${workspaceSlug}/${leaf.leaf_key}`)}
            >
              <span className="ib__card-kicker">{leaf.institution}</span>
              <span className="ib__card-title">{leaf.display_name || leaf.subject_name}</span>
              <span className="ib__card-meta">{courseCounts(leaf)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
